/**
 * Shared keystroke formatting utilities
 * Maps special key names to unicode symbols and provides React components
 * for consistent keystroke rendering across all views.
 */
import React from 'react';

/**
 * Comprehensive mapping of special key names to unicode symbols/short labels.
 * Used everywhere keystrokes are displayed.
 */
export const SPECIAL_KEY_MAP = {
    // Whitespace / editing
    'Enter': '↵',
    'Backspace': '⌫',
    'Delete': '⌦',
    'Tab': '⇥',
    'Space': '␣',
    'Escape': 'Esc',

    // Arrow keys
    'ArrowUp': '↑',
    'ArrowDown': '↓',
    'ArrowLeft': '←',
    'ArrowRight': '→',

    // Navigation
    'Home': '⇤',
    'End': '⇥',
    'PageUp': '⇞',
    'PageDown': '⇟',

    // Modifiers
    'Shift': '⇧',
    'Control': 'Ctrl',
    'Alt': 'Alt',
    'Meta': '⌘',
    'CapsLock': '⇪',

    // Function keys
    'F1': 'F1', 'F2': 'F2', 'F3': 'F3', 'F4': 'F4',
    'F5': 'F5', 'F6': 'F6', 'F7': 'F7', 'F8': 'F8',
    'F9': 'F9', 'F10': 'F10', 'F11': 'F11', 'F12': 'F12',

    // Other
    'Insert': 'Ins',
    'PrintScreen': 'PrtSc',
    'ScrollLock': 'ScrLk',
    'Pause': 'Pause',
    'NumLock': 'NumLk',
    'ContextMenu': '☰',
};

// Keys that are modifiers (should be ignored in keystroke text rendering)
export const MODIFIER_KEYS = new Set([
    'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'NumLock', 'ScrollLock'
]);

/**
 * Check if a key name is a special (non-character) key
 */
export function isSpecialKey(key) {
    return SPECIAL_KEY_MAP[key] !== undefined || key.length > 1;
}

/**
 * Get the display symbol for a key. Returns null for regular single characters.
 */
export function getKeySymbol(key) {
    return SPECIAL_KEY_MAP[key] || null;
}

/**
 * SpecialKeyBadge - Inline badge for special keys (Enter, Tab, arrows, etc.)
 * Renders as a small styled pill with the key symbol.
 */
export const SpecialKeyBadge = ({ keyName, symbol, className = '' }) => {
    const display = symbol || SPECIAL_KEY_MAP[keyName] || keyName;

    // Determine style variant based on key type
    let variant = 'default';
    if (keyName === 'Enter' || keyName === '\n') variant = 'enter';
    else if (keyName === 'Tab') variant = 'tab';
    else if (keyName === 'Backspace' || keyName === 'Delete') variant = 'delete';

    return (
        <span
            className={`keystroke-key-badge badge-${variant} ${className}`}
            data-tooltip={keyName}
        >
            {display}
        </span>
    );
};

/**
 * Process a keystroke buffer string for the status strip.
 * Handles Backspace by removing previous chars, converts special keys to symbols.
 * Returns an array of tokens: { type: 'char'|'special'|'deleted', value, key? }
 */
export function processKeystrokeText(text) {
    if (!text) return [];

    // Split text by special key markers like [Enter], [Backspace], etc.
    // Also handle bare key names that appear in keystroke streams
    const specialKeyPattern = /(\[(?:Enter|Backspace|Delete|Tab|Escape|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Home|End|PageUp|PageDown|Insert|F[1-9]|F1[0-2]|Shift|Control|Alt|Meta|CapsLock|Space|PrintScreen|ScrollLock|Pause|NumLock|ContextMenu)\])/gi;

    const parts = text.split(specialKeyPattern);
    const tokens = [];

    for (const part of parts) {
        if (!part) continue;

        // Check if this is a bracketed special key
        const bracketMatch = part.match(/^\[(.+)\]$/i);
        if (bracketMatch) {
            const keyName = bracketMatch[1];
            // Normalize case
            const normalizedKey = Object.keys(SPECIAL_KEY_MAP).find(
                k => k.toLowerCase() === keyName.toLowerCase()
            );

            if (normalizedKey === 'Backspace') {
                // Mark last non-deleted char token as deleted
                for (let j = tokens.length - 1; j >= 0; j--) {
                    if (tokens[j].type === 'char') {
                        tokens[j].type = 'deleted';
                        break;
                    }
                }
            } else if (normalizedKey && !MODIFIER_KEYS.has(normalizedKey)) {
                tokens.push({ type: 'special', value: SPECIAL_KEY_MAP[normalizedKey], key: normalizedKey });
            }
            continue;
        }

        // Regular text characters
        for (const ch of part) {
            tokens.push({ type: 'char', value: ch });
        }
    }

    return tokens;
}

/**
 * Render a processed token array as React elements for the status strip.
 */
export function renderKeystrokeTokens(tokens) {
    return tokens.map((token, i) => {
        if (token.type === 'special') {
            return (
                <SpecialKeyBadge key={i} keyName={token.key} symbol={token.value} className="strip-key-badge" />
            );
        }
        if (token.type === 'deleted') {
            return (
                <span key={i} className="strip-char-deleted">{token.value}</span>
            );
        }
        return token.value;
    });
}
